import * as React from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EventCalendar,
  type EventCalendarEvent,
  type EventCalendarLabels,
} from '@org/ui';
import { EventDeleteConfirmDialog } from './event-delete-confirm-dialog';

// The generic month calendar now lives in @org/ui (shared with the student
// platform); this file keeps the admin-only affordances: create on empty
// days, per-event delete with series confirmation, and admin i18n labels.
export type EventCalendarEntry = EventCalendarEvent;

type DeleteConfirmState = {
  event: EventCalendarEntry;
  isOpen: boolean;
};

type EventCalendarAdminProps = {
  month: Date;
  onMonthChange?: (date: Date) => void;
  events?: EventCalendarEntry[];
  onSelectEvent?: (eventId: string) => void;
  onCreateEvent?: (date: Date) => void;
  onDeleteEvent?: (eventId: string, deleteFutureOccurrences: boolean) => void;
};

export function EventCalendarAdmin({
  month,
  onMonthChange,
  events = [],
  onSelectEvent,
  onCreateEvent,
  onDeleteEvent,
}: EventCalendarAdminProps) {
  const { t } = useTranslation('admin');
  const [deleteConfirmState, setDeleteConfirmState] = React.useState<DeleteConfirmState | null>(null);

  const labels = React.useMemo<EventCalendarLabels>(
    () => ({
      more: (count) => t('events.calendar.more', { count }),
      dayEventsCount: (count) => t('events.calendar.count', { count }),
      noEvents: t('events.calendar.noEvents'),
      noEventsForDay: t('events.calendar.noEventsForDay'),
      addEvent: t('events.calendar.add'),
      kind: (kind) => t(`events.calendar.kinds.${kind}`, { defaultValue: kind.replace('-', ' ') }),
    }),
    [t],
  );

  const renderEventAction = React.useCallback(
    (event: EventCalendarEvent) => (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="flex-shrink-0 text-destructive hover:text-destructive"
        onClick={() => setDeleteConfirmState({ event, isOpen: true })}
      >
        <Trash2 className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">{t('events.calendar.deleteAction')}</span>
      </Button>
    ),
    [t],
  );

  const isSeriesEvent = deleteConfirmState
    ? Boolean(deleteConfirmState.event.seriesTemplateId || deleteConfirmState.event.isTemplate)
    : false;

  const handleConfirmDelete = React.useCallback(
    (deleteSeries: boolean) => {
      if (!deleteConfirmState || !onDeleteEvent) return;

      const targetEventId = deleteSeries
        ? (deleteConfirmState.event.isTemplate
            ? deleteConfirmState.event.eventId
            : (deleteConfirmState.event.seriesTemplateId ?? deleteConfirmState.event.eventId))
        : deleteConfirmState.event.eventId;

      onDeleteEvent(targetEventId, deleteSeries);
      setDeleteConfirmState(null);
    },
    [deleteConfirmState, onDeleteEvent],
  );

  return (
    <>
      <EventCalendar
        month={month}
        onMonthChange={onMonthChange}
        events={events}
        labels={labels}
        onSelectEvent={onSelectEvent}
        onCreateEvent={onCreateEvent}
        renderEventAction={renderEventAction}
      />

      {/* Delete Confirmation Dialog - Single instance for all calendar days */}
      {deleteConfirmState && (
        <EventDeleteConfirmDialog
          open={deleteConfirmState.isOpen}
          onOpenChange={(open) => {
            if (!open) setDeleteConfirmState(null);
          }}
          eventTitle={deleteConfirmState.event.title}
          isSeriesEvent={isSeriesEvent}
          onConfirmDelete={handleConfirmDelete}
        />
      )}
    </>
  );
}
